import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router/composables';
import { capitalize } from 'lodash';
import { AxiosError } from 'axios';
import { useStore } from '@/store';
import { getUser, isStoreAuthenticated } from '@/api/shopify/user';
import { StoreType } from '@extTypes/api/customily/user';
import {
    isWooCommerceStore,
    isEtsyStore as checkIsEtsyStore,
    isShoplazzaStore,
    isAmazonStore as checkIsAmazonStore,
    getStoreType
} from '@/utils/stores';
import { getEtsyLogin, setEtsyRequestToken } from '@/api/shopify/etsy';
import { forceStoreResubscribe } from '@/api/shopify/payment';
import StoreSelector from '@/components/StoreSelector.vue';
import config from '@/config/config';


export default /*#__PURE__*/_defineComponent({
  __name: 'App',
  setup(__props) {

const { state, commit } = useStore();
const route = useRoute();
const router = useRouter();

const wideRoutes = new Set(['/products_preview']);

const storeAuthenticated = ref(true);
const isOpenRoute = ref(false);
const isEtsyStore = ref(false);
const isAmazonStore = ref(false);
const omitClasses = ref(false);
const loginUrl = ref('');
const tokenSecret = ref('');
const tokenState = ref('');
const billingResolving = ref(false);
const billingError = ref('');

const user = computed(() => state.user);
const store = computed(() => state.store);

const appInstallUrl = computed((): string => {
    if (!store.value || !user.value?.customilyUser) return '';

    const baseUrl = config.APP_SHOPIFY_BASE_URL;
    const shopifyProductPersonalizerBaseUrl = config.SHOPIFY_PRODUCT_PERSONALIZER_BASE_URL;
    const userStores = user.value.customilyUser.Stores;

    if (isWooCommerceStore(store.value, userStores)) return `https://${store.value}/wp-login.php`;
    if (isEtsyStore.value) return loginUrl.value;
    if (isShoplazzaStore(store.value, userStores)) return `${baseUrl}/shoplazza/auth?shop=${store.value}`;
    if (isAmazonStore.value) return `${baseUrl}/amazon/auth?shop=${store.value}`;
    return `${shopifyProductPersonalizerBaseUrl}/auth/login?shop=${store.value}`;
});

const msgVerb = computed(() => {
    const verb = isEtsyStore.value ? 'connect' : 'install';
    return {
        present: verb,
        past: `${verb}ed`,
        capitalized: capitalize(verb)
    };
});

const wooUserHasntInstalledYet = computed(() => {
    const isWooUser = Boolean(new URL(location.href).searchParams.get('isWooUser'));
    //this means that our api call throwed 401 when trying to get the user, because the user hasn't installed
    return isWooUser && !user.value;
});

/**
 * Store the current page is authenticating/gating for: the shop query param when present, otherwise the
 * selected store. Single source of truth so the store authentication check, the billing wall decision and
 * its message always refer to the same store.
 */
const currentShop = computed((): string => (route.query.shop as string) || store.value);

const showBillingWall = computed((): boolean => {
    if (isOpenRoute.value || !storeAuthenticated.value || !user.value || !currentShop.value) {
        return false;
    }
    return user.value.customilyUser.Stores.find((s) => s.Url === currentShop.value)?.BillingBlocked ?? false;
});

// Shopify and Shoplazza stores can self-resolve billing from the app.
// Every other store type is directed to contact support instead.
const billingWallStoreCanSubscribe = computed((): boolean => {
    const type = currentShop.value
        ? getStoreType(currentShop.value, user.value?.customilyUser.Stores ?? [])
        : undefined;
    return type === StoreType.Shopify || type === StoreType.Shoplazza;
});

const billingSubscriptionUrl = computed((): string => {
    if (!currentShop.value || !user.value) return '';
    const type = getStoreType(currentShop.value, user.value.customilyUser.Stores);

    if (type === StoreType.Shoplazza) {
        return `${config.APP_SHOPIFY_BASE_URL}/shoplazza/auth?shop=${currentShop.value}&useAppScopes=true`;
    }

    // Shopify goes through the product-personalizer app managed-install entry point
    return `${config.SHOPIFY_PRODUCT_PERSONALIZER_BASE_URL}/auth/login?shop=${currentShop.value}`;
});

const isWideRoute = computed(() => {
    const currentRoute = route.path || '';
    return wideRoutes.has(currentRoute) || currentRoute.startsWith('/reviewProducts/');
});

const openBillingSubscriptionLink = async () => {
    if (billingResolving.value || !currentShop.value) return;

    billingResolving.value = true;
    billingError.value = '';
    try {
        await forceStoreResubscribe(currentShop.value, false);
        (window.top ?? window).location.href = billingSubscriptionUrl.value;
    } catch (error) {
        billingError.value = 'Something went wrong. Please try again or contact hello@customily.com.';
        window.captureException(error as Error);
        billingResolving.value = false;
    }
};

const openInstallLink = (_event: MouseEvent, region = '') => {
    if (isEtsyStore.value) setEtsyRequestToken(tokenSecret.value, tokenState.value, store.value);

    let newWindow = appInstallUrl.value;
    if (region) {
        newWindow += `&region=${region}`;
    }
    window.open(newWindow, '_blank');
};

const checkStoreAuthentication = async () => {
    if (!store.value || !user.value) {
        storeAuthenticated.value = true;
        return;
    }

    try {
        const shop = currentShop.value;
        const storeAuth = await isStoreAuthenticated(shop);

        if (!storeAuth.authenticated) {
            storeAuthenticated.value = false;
        } else {
            storeAuthenticated.value = true;
            if (window.Appcues) {
                const appCuesUsername = user.value?.customilyUser.Username + process.env.VUE_APP_APPCUES_SUFIX;
                commit('setShowShopifyFunction', storeAuth.showShopifyFunction);
                window.Appcues.identify(appCuesUsername, {
                    createdAt: new Date(user.value?.customilyUser?.CreatedDate as string).getTime(),
                    accountId: user.value?.customilyUser?.Id,
                    version: '2.0', // users on different versions may need to see different content
                    language: 'english', // for multi-language applications
                    stores: user.value?.customilyUser?.Stores.map((s) => `_${s.Type}_`).join(',')
                });
            }
        }

        isEtsyStore.value = checkIsEtsyStore(shop, user.value.customilyUser.Stores);
        if (isEtsyStore.value && !storeAuthenticated.value) {
            const etsyLogin = await getEtsyLogin();
            loginUrl.value = etsyLogin.url;
            tokenSecret.value = etsyLogin.codeVerification;
            tokenState.value = etsyLogin.state;
        }
        isAmazonStore.value = checkIsAmazonStore(shop, user.value.customilyUser.Stores);
    } catch (_error) {
        const error = _error as AxiosError;
        if (error.request.status !== 401) {
            window.captureException(error as Error);
        }
        storeAuthenticated.value = false;
    }
};

const checkStoreSelectorVisibility = (path: string) => {
    const allowAllStoresPaths = ['/sets'];
    const allowAllStoresSelector = allowAllStoresPaths.includes(path);

    /**
     * On routes that should not be able to select 'All Stores' option,
     * select the first available store if the current one is null
     */
    if (!allowAllStoresSelector && !store.value && user.value) {
        const url = user.value.customilyUser.Stores.find(
            (s) => s.Type === StoreType.Shopify || s.Type === StoreType.WooCommerce || s.Type === StoreType.Shoplazza
        )?.Url;
        if (!url) {
            console.error('User does not have any Shopify stores!');
            return;
        }
        commit('setStore', url);
    }
};

const getUserData = async () => {
    if (isOpenRoute.value) return;

    try {
        await getUser();
        await checkStoreAuthentication();
    } catch {
        storeAuthenticated.value = false;
    }
};

watch(store, () => {
    checkStoreAuthentication();
});

const {
    route: {
        matched: [parent]
    }
} = router.resolve({ path: location.pathname });
isOpenRoute.value = route.meta?.isOpen || !parent?.meta?.requireStore;
omitClasses.value = route.meta?.omitClasses || parent?.meta?.omitClasses;
checkStoreSelectorVisibility(router.currentRoute.path);

document.addEventListener('auth-error', () => {
    storeAuthenticated.value = false;
});

getUserData();

onMounted(() => {
    router.beforeEach((to, _from, next) => {
        isOpenRoute.value = route.meta?.isOpen;
        checkStoreSelectorVisibility(to.path);
        next();
    });
});

return { __sfc: true,state, commit, route, router, wideRoutes, storeAuthenticated, isOpenRoute, isEtsyStore, isAmazonStore, omitClasses, loginUrl, tokenSecret, tokenState, billingResolving, billingError, user, store, appInstallUrl, msgVerb, wooUserHasntInstalledYet, currentShop, showBillingWall, billingWallStoreCanSubscribe, billingSubscriptionUrl, isWideRoute, openBillingSubscriptionLink, openInstallLink, checkStoreAuthentication, checkStoreSelectorVisibility, getUserData, parent, StoreSelector }
}

})